// src/SetupWizard/Step2Tone.js
import React, { useState, useEffect } from 'react';
import { Box, TextField, Chip, Paper, Typography, Button, CircularProgress } from '@mui/material';
import axios from 'axios';
import { getApiUrl } from '../config';

function Step2Tone({ tone, onChange }) {
  const [selectedTones, setSelectedTones] = useState(tone ? tone.split('+').map(t => t.trim()).filter(t => t) : []);
  const [customInput, setCustomInput] = useState(tone || '');
  const [suggestedTone, setSuggestedTone] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState('');
  const [error, setError] = useState('');

  const toneSuggestions = {
    professional: [
      'Professional & Authoritative',
      'Expert & Educational',
      'Analytical & Detailed',
      'Technical & Precise'
    ],
    casual: [
      'Friendly & Conversational',
      'Casual & Relatable',
      'Fun & Engaging',
      'Humorous & Light'
    ],
    creative: [
      'Creative & Inspiring',
      'Storytelling & Narrative',
      'Personal & Authentic',
      'Emotional & Expressive'
    ]
  };

  useEffect(() => {
    // Validate initial state
    validateAndUpdate(selectedTones);
  }, []);

  const validateAndUpdate = (tones) => {
    if (!tones.length) {
      setError('Please select at least one tone for your blog');
      onChange({ tone: customInput, isValid: false });
    } else {
      setError('');
      onChange({ tone: tones.join(' + '), isValid: true });
    }
  };

  const handleAnalyzeTone = async () => {
    setIsAnalyzing(true);
    setAnalyzeError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${getApiUrl()}/settings/analyze-tone`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.suggested_tone) {
        setSuggestedTone(response.data.suggested_tone);
      }
    } catch (error) {
      console.error('Error analyzing tone:', error);
      setAnalyzeError('Failed to analyze tone. Please try again or select manually.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleUseSuggested = () => {
    if (selectedTones.length < 3) {
      const newTones = [...selectedTones, suggestedTone];
      setSelectedTones(newTones);
      setCustomInput(newTones.join(' + '));
      validateAndUpdate(newTones);
      setSuggestedTone('');
    }
  };

  const handleToneSelect = (selectedTone) => {
    let newTones;
    if (selectedTones.includes(selectedTone)) {
      // Remove tone if already selected
      newTones = selectedTones.filter(tone => tone !== selectedTone);
    } else {
      // Add tone if not already selected (limit to 3 tones)
      if (selectedTones.length < 3) {
        newTones = [...selectedTones, selectedTone];
      } else {
        return; // Don't add if already at limit
      }
    }
    setSelectedTones(newTones);
    setCustomInput(newTones.join(' + '));
    validateAndUpdate(newTones);
  };

  const handleDeleteTone = (toneToDelete) => {
    const newTones = selectedTones.filter(tone => tone !== toneToDelete);
    setSelectedTones(newTones);
    setCustomInput(newTones.join(' + '));
    validateAndUpdate(newTones);
  };

  // Handle custom input changes
  const handleCustomInputChange = (e) => {
    const input = e.target.value;
    setCustomInput(input);
    
    // Split by '+' and trim each tone
    const tones = input.split('+').map(t => t.trim()).filter(t => t);
    
    // Only update if we're within the limit
    if (tones.length <= 3) {
      setSelectedTones(tones);
      validateAndUpdate(tones);
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Step 2: Set Your Writing Tone
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 3 }}>
        Your blog's tone of voice helps establish your brand personality. Select up to 3 tones to create your unique voice.
      </Typography>

      {/* Custom Input Field */}
      <TextField
        fullWidth
        label="Writing Tone"
        value={customInput}
        onChange={handleCustomInputChange}
        placeholder="Type custom tones or select from below (separate multiple tones with '+')"
        helperText={error || `${selectedTones.length}/3 tones selected. Example: Professional + Friendly`}
        error={!!error}
        sx={{ mb: 3 }}
      />

      {/* Tone Analysis Section */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: '#f0f7ff', borderRadius: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Have existing blog posts?
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          We can analyze your existing content to suggest a matching tone.
        </Typography>
        <Button
          variant="contained"
          onClick={handleAnalyzeTone}
          disabled={isAnalyzing}
          startIcon={isAnalyzing ? <CircularProgress size={20} /> : null}
        >
          {isAnalyzing ? 'Analyzing...' : 'Analyze My Content'}
        </Button>
        {analyzeError && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {analyzeError}
          </Typography>
        )}
      </Paper>

      {/* Suggested Tone Display */}
      {suggestedTone && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#e3f2fd', borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Suggested Tone:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip 
              label={suggestedTone} 
              color="primary" 
              onClick={handleUseSuggested}
              disabled={selectedTones.length >= 3}
              sx={{ cursor: 'pointer' }}
            />
            <Typography variant="body2" color="text.secondary">
              Click to add this tone
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Predefined Tone Suggestions */}
      {Object.entries(toneSuggestions).map(([category, tones]) => (
        <Box key={category} sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ textTransform: 'capitalize' }}>
            {category} Tones
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {tones.map((tone) => (
              <Chip
                key={tone}
                label={tone}
                onClick={() => handleToneSelect(tone)}
                variant={selectedTones.includes(tone) ? "filled" : "outlined"}
                color={selectedTones.includes(tone) ? "primary" : "default"}
                sx={{ cursor: 'pointer' }}
                disabled={selectedTones.length >= 3 && !selectedTones.includes(tone)}
              />
            ))}
          </Box>
        </Box>
      ))}

      {/* Tips Section */}
      <Paper sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          💡 Tips for combining tones:
        </Typography>
        <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
          <li>Mix professional and casual tones for an approachable expert voice</li>
          <li>Combine creative with technical tones for engaging educational content</li>
          <li>Balance authoritative with personal tones for relatable expertise</li>
          <li>Keep your combination consistent across your content</li>
        </ul>
      </Paper>
    </Box>
  );
}

export default Step2Tone;
